import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Eye, EyeOff, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) {
      setErr("Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (pw !== pw2) {
      setErr("Passwörter stimmen nicht überein.");
      return;
    }
    if (!token) {
      setErr("Kein Reset-Code in der URL.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/campus/login", { replace: true }), 2400);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Link ungültig oder abgelaufen.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-cream-200 text-terra">
            <AlertCircle className="h-7 w-7" strokeWidth={1.6} />
          </div>
          <h1 className="font-display text-sand-800 text-3xl font-semibold mt-6">Kein Reset-Code.</h1>
          <p className="mt-3 text-sand-600 text-[14.5px]">
            Bitte nutze den Link aus deiner E-Mail oder fordere einen neuen an.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-12 px-6 text-[14.5px] font-medium"
          >
            Neuen Link anfordern →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md" data-testid="reset-password-card">
        <Link to="/campus/login" className="text-[12px] uppercase tracking-[0.32em] font-medium text-sand-500 mb-10 block">
          ← Anmelden
        </Link>

        {done ? (
          <div data-testid="reset-password-done">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-terra/10 text-terra">
              <CheckCircle2 className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <h1 className="font-display text-sand-800 text-3xl font-semibold mt-6">Passwort aktualisiert.</h1>
            <p className="mt-3 text-sand-600 text-[14.5px]">
              Du wirst gleich zur Anmeldung weitergeleitet …
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-sand-800 text-3xl font-semibold leading-tight">
              Neues Passwort
            </h1>
            <p className="mt-2 text-sand-600 text-[14.5px]">
              Mindestens 8 Zeichen. Der Reset-Link wird einmalig verwendet.
            </p>

            <form onSubmit={submit} className="mt-10 grid gap-4" data-testid="reset-password-form">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">
                  Neues Passwort
                </label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    minLength={8}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    data-testid="reset-password-new"
                    className="w-full rounded-xl border border-cream-300 bg-white px-4 pr-12 h-12 text-[15px] outline-none focus:border-sand-800 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center text-sand-500 hover:text-sand-800"
                    aria-label="Passwort anzeigen"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.6} /> : <Eye className="h-4 w-4" strokeWidth={1.6} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">
                  Passwort wiederholen
                </label>
                <input
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="w-full rounded-xl border border-cream-300 bg-white px-4 h-12 text-[15px] outline-none focus:border-sand-800 transition-colors"
                />
              </div>

              {err && (
                <p className="text-[13px] text-terra font-medium" data-testid="reset-password-error">{err}</p>
              )}

              <Button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit"
                className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-12 px-6 text-[14.5px] font-medium mt-2 disabled:opacity-60"
              >
                <span className="inline-flex items-center gap-2">
                  {busy ? "Speichern…" : "Passwort speichern"}
                  {!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.6} />}
                </span>
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
